import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import { IconButton, RyeconMagnifyingGlass } from '@dbt-labs/sourdough';

import { useFilterContext } from '@/context/FilterProvider';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import {
  isSubmitKey,
  MAXIMUM_QUERY_LENGTH,
  QUERY_TOO_LONG_MESSAGE,
} from '@/util/input';

export const SearchInput = () => {
  const [params] = useSearchParams();
  const rawSearchParam = params.get('search');
  const [searchInput, setSearchInput] = useState(rawSearchParam ?? '');
  const navigate = useNavigate();
  const searchResultsLink = useLinkToExplorer('searchResults');
  const projectScope = useUrlProjectParams();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const searchInputRef = useRef(searchInput);
  searchInputRef.current = searchInput;
  const isQueryTooLong = searchInput.length > MAXIMUM_QUERY_LENGTH;
  const filterContext = useFilterContext();
  useEffect(() => {
    setSearchInput(rawSearchParam ?? '');
  }, [rawSearchParam]);

  const className = twJoin(
    'transition-color group mb-2 flex h-11 overflow-hidden rounded-md border duration-100 motion-reduce:duration-0',
    isQueryTooLong
      ? 'border-borderDanger'
      : isInputFocused
        ? 'input-focused border-borderBrand'
        : 'border-borderMain',
  );

  return (
    <div aria-haspopup="true" aria-controls="searchMenu">
      <div className={className}>
        <span className="flex-0 m-3 mb-0 mr-2 text-sm text-fgMain">
          <IconButton
            ryecon={RyeconMagnifyingGlass}
            size="sm"
            onClick={() => inputRef.current?.focus()}
          />
        </span>
        <span className="flex flex-1 overflow-hidden rounded-md bg-bgMain">
          <input
            data-testid="panel-search"
            ref={inputRef}
            className="my-1 mr-1 h-8 w-full flex-1 border-transparent bg-bgMain bg-none px-1 placeholder-fgDecorative focus:border-transparent focus:outline-none focus:ring-0"
            value={searchInput}
            placeholder={'Search for resources and columns'}
            onFocus={() => {
              setIsInputFocused(true);
            }}
            onBlur={() => setIsInputFocused(false)}
            onChange={(e: any) => {
              const input: string = e.target.value;
              setSearchInput(input);
            }}
            onKeyDown={(e: { code: string }) => {
              if (!isSubmitKey(e)) {
                return;
              }
              if (isQueryTooLong) return;
              // get all the existing qs params and update the search param with search input
              navigate(
                searchResultsLink({
                  ...projectScope,
                  search: searchInput,
                  filters: JSON.stringify(filterContext.filters),
                }),
              );

              inputRef.current?.blur();
            }}
          />
        </span>
      </div>
      {isQueryTooLong && (
        <p className="mt-1 text-xs text-fgDanger">{QUERY_TOO_LONG_MESSAGE}</p>
      )}
    </div>
  );
};
